import { XMLParser } from 'fast-xml-parser';

import {
	contactEvents,
	RegContact,
	ReginfoData,
	ReginfoDocument,
	ReginfoRegistration,
	registrationStates,
	RegSnapshot,
} from '../model/types';

// ---- Мердж частичных уведомлений (RFC 3680) ----
/**
 * Применяет частичное reginfo-уведомление к предыдущему снапшоту регистрации.
 *
 * @param prev Предыдущий снапшот или null.
 * @param xml XML тела NOTIFY с reginfo.
 * @returns Обновлённый RegSnapshot.
 */
export function applyRegPatch(prev: RegSnapshot | null, xml: string): RegSnapshot {
	const patch = parseReginfoXML(xml);

	// Базовый снимок.
	let base: RegSnapshot = prev ?? { aor: patch.aor, version: 0, contacts: [] };

	// Если пришёл "full" — полностью заменяем.
	if (patch.rootState === 'full') {
		const alive = patch.contacts.filter((c) => c.state !== 'terminated');
		return {
			aor: patch.aor,
			version: patch.version,
			contacts: alive,
			terminated: patch.regState === 'terminated',
		};
	}

	// partial: обновляем по id контактов и состоянию всей регистрации
	const map = new Map<string, RegContact>(base.contacts.map((c) => [c.id, c]));
	for (const c of patch.contacts) {
		if (c.state === 'terminated') {
			map.delete(c.id);
		} else {
			map.set(c.id, { ...map.get(c.id), ...c });
		}
	}
	base = {
		aor: patch.aor || base.aor,
		version: patch.version,
		contacts: [...map.values()],
		terminated: patch.regState === 'terminated',
	};
	return base;
}

// Удобный булев флаг «зарегистрирован ли кто-то по этому AOR»
/**
 * Проверяет, есть ли хотя бы один активный контакт в снапшоте регистрации.
 *
 * @param s Снимок регистрации.
 * @returns true, если есть активная регистрация.
 */
export function isAorRegistered(s: RegSnapshot): boolean {
	if (s.terminated) return false;
	return s.contacts.some((c) => c.state === 'active' && (c.expires == null || c.expires > 0));
}

const DEFAULT_XMLNS = 'urn:ietf:params:xml:ns:reginfo';

const contactEventSet = new Set(contactEvents);
const registrationStateSet = new Set(registrationStates);

const xmlParser = new XMLParser({
	ignoreAttributes: false,
	attributeNamePrefix: '',
	trimValues: true,
	textNodeName: '#text',
	transformAttributeName: (attrName: string) =>
		attrName.replaceAll(/-([A-Za-z])/g, (_match, ch: string) => ch.toUpperCase()),
	isArray: (_name: string, jpath: string) =>
		jpath === 'reginfo.registration' || jpath === 'reginfo.registration.contact',
});

/**
 * Нормализует поле XML, которое может быть массивом, объектом или undefined.
 */
const ensureArray = <T>(value: T | T[] | undefined): T[] =>
	Array.isArray(value) ? value : value == null ? [] : [value];

/**
 * Безопасно извлекает строковое значение из XML-узла.
 */
const toStringValue = (value: unknown): string | undefined => {
	if (value == null) return undefined;
	if (typeof value === 'string') {
		const trimmed = value.trim();
		return trimmed.length > 0 ? trimmed : undefined;
	}
	if (typeof value === 'number') {
		return Number.isFinite(value) ? String(value) : undefined;
	}
	if (typeof value === 'object' && '#text' in (value as Record<string, unknown>)) {
		return toStringValue((value as Record<string, unknown>)['#text']);
	}
	return undefined;
};

/**
 * Преобразует значение в неотрицательное число, иначе возвращает undefined.
 */
const toNonNegativeNumber = (value: unknown): number | undefined => {
	const asString = toStringValue(value);
	if (asString == null) return undefined;
	const num = Number(asString);
	return Number.isFinite(num) && num >= 0 ? num : undefined;
};

/**
 * Нормализует state регистрации к допустимым RFC-значениям.
 */
const normalizeRegistrationState = (value: unknown): 'active' | 'terminated' => {
	const candidate = toStringValue(value)?.toLowerCase();
	return registrationStateSet.has(candidate as any)
		? (candidate as 'active' | 'terminated')
		: 'active';
};

/**
 * Нормализует root-state reginfo к `full`/`partial`.
 */
const normalizeRootState = (value: unknown): 'full' | 'partial' =>
	toStringValue(value)?.toLowerCase() === 'partial' ? 'partial' : 'full';

/**
 * Нормализует event контакта и отбрасывает неподдерживаемые значения.
 */
const normalizeContactEvent = (value: unknown) => {
	const candidate = toStringValue(value)?.toLowerCase();
	return contactEventSet.has(candidate as any) ? (candidate as RegContact['event']) : undefined;
};

/**
 * Генерирует fallback-id для узлов без явного `id`.
 */
const generateId = (): string =>
	typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
		? crypto.randomUUID()
		: Math.random().toString(36).slice(2);

// ---- Парсер reginfo ----
/**
 * Разбирает XML reginfo (RFC 3680) в структуру ReginfoData.
 *
 * @param xml Тело NOTIFY.
 * @returns Спарсенная информация о регистрации.
 */
export function parseReginfoXML(xml: string): ReginfoData {
	const raw = xmlParser.parse(xml) as Record<string, any> | undefined;
	const reginfoNode = raw?.reginfo ?? {};
	const registrationsRaw = ensureArray(reginfoNode.registration);

	const registrations: ReginfoRegistration[] = registrationsRaw
		.map((reg: any) => {
			const contacts = ensureArray(reg?.contact)
				.map((contact: any) => {
					const uri = toStringValue(contact?.uri) ?? '';
					return {
						id: toStringValue(contact?.id) ?? generateId(),
						uri,
						state: normalizeRegistrationState(contact?.state),
						event: normalizeContactEvent(contact?.event),
						durationRegistered: toNonNegativeNumber(contact?.durationRegistered),
						expires: toNonNegativeNumber(contact?.expires),
					};
				})
				.filter((contact) => contact.uri.length > 0);

			return {
				aor: toStringValue(reg?.aor) ?? '',
				id: toStringValue(reg?.id) ?? generateId(),
				state: normalizeRegistrationState(reg?.state),
				contacts,
			};
		})
		.filter((reg) => reg.aor.length > 0);

	const reginfoDoc: ReginfoDocument = {
		xmlns: toStringValue(reginfoNode?.xmlns) ?? DEFAULT_XMLNS,
		version: toNonNegativeNumber(reginfoNode?.version) ?? 0,
		state: normalizeRootState(reginfoNode?.state),
		registrations,
	};

	const primaryRegistration = reginfoDoc.registrations[0];

	let regInfoData: ReginfoData = {
		version: reginfoDoc.version,
		rootState: reginfoDoc.state,
		aor: '',
		regState: 'terminated',
		contacts: [],
	};

	if (!primaryRegistration) {
		return regInfoData;
	}

	const contacts: RegContact[] = primaryRegistration.contacts.map((contact) => ({
		id: contact.id,
		uri: contact.uri,
		state: contact.state,
		event: contact.event,
		durationRegistered: contact.durationRegistered,
		expires: contact.expires,
	}));

	regInfoData = {
		...regInfoData,
		aor: primaryRegistration.aor,
		regState: primaryRegistration.state,
		contacts,
	};

	return regInfoData;
}

/** Заголовок Accept для reginfo (RFC 3680). */
export const REG_ACCEPT = 'application/reginfo+xml';

/** Имя event-package для уведомлений регистрации. */
export const REG_EVENT = 'reg';
